
import React from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { ExternalLink, Search } from 'lucide-react';
import { Patient } from './types';
import LoaderComponent from '@/components/common/LoaderComponent';

interface PatientTableProps {
  patients: Patient[];
  paginatedPatients: Patient[];
  startIndex: number;
  rowsPerPage: number;
  onPatientClick: (patient: Patient) => void;
  isLoading?: boolean;
}

const PatientTable = ({ patients, paginatedPatients, startIndex, rowsPerPage, onPatientClick, isLoading = false }: PatientTableProps) => {
  return (
    <Card id="table-search-patients-container">
      <CardContent className="p-0">
        <div className="p-6 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <h3 id="table-search-patients-title" className="text-lg font-semibold text-gray-900">
              Search Results
            </h3>
            {patients.length > 0 && (
              <div id="table-search-patients-pagination-info" className="text-sm text-gray-500">
                Showing {startIndex + 1}-{Math.min(startIndex + rowsPerPage, patients.length)} of {patients.length}
              </div>
            )}
          </div>
        </div>

        {isLoading ? (
          <LoaderComponent
            size="lg"
            text="Searching for patients..."
            className="min-h-[300px]"
          />
        ) : (
          <>
            <div className="overflow-x-auto">
              <Table id="table-search-patients">
                <TableHeader>
                  <TableRow id="table-search-patients-header-row">
                    <TableHead id="header-cell-full-name" className="text-gray-700 font-semibold">Full Name</TableHead>
                    <TableHead id="header-cell-dob" className="text-gray-700 font-semibold">DOB</TableHead>
                    <TableHead id="header-cell-mobile" className="text-gray-700 font-semibold">Mobile Number</TableHead>
                    <TableHead id="header-cell-home" className="text-gray-700 font-semibold">Home Number</TableHead>
                    <TableHead id="header-cell-email" className="text-gray-700 font-semibold">Email</TableHead>
                    <TableHead id="header-cell-clinic" className="text-gray-700 font-semibold">Clinic Name</TableHead>
                    <TableHead id="header-cell-emr" className="text-gray-700 font-semibold">EMR</TableHead>
                    <TableHead id="header-cell-actions" className="text-gray-700 font-semibold">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody id="table-search-patients-body">
                  {paginatedPatients.map((patient) => (
                    <TableRow
                      key={patient.pep_patient_id}
                      className="hover:bg-gray-50"
                      id={`row-patient-${patient.pep_patient_id}`}
                      data-patient-id={patient.pep_patient_id}
                    >
                      <TableCell className="font-medium text-gray-900" id={`cell-patient-${patient.pep_patient_id}-name`}>{patient.full_name}</TableCell>
                      <TableCell className="text-gray-700" id={`cell-patient-${patient.pep_patient_id}-dob`}>{patient.dob}</TableCell>
                      <TableCell className="text-gray-700" id={`cell-patient-${patient.pep_patient_id}-mobile`}>{patient.mobile_phone_number || '-'}</TableCell>
                      <TableCell className="text-gray-700" id={`cell-patient-${patient.pep_patient_id}-home`}>{patient.home_phone_number || '-'}</TableCell>
                      <TableCell className="text-gray-700" id={`cell-patient-${patient.pep_patient_id}-email`}>{patient.email || '-'}</TableCell>
                      <TableCell className="text-gray-700 max-w-[200px] truncate" title={patient.clinic_name} id={`cell-patient-${patient.pep_patient_id}-clinic`}>{patient.clinic_name}</TableCell>
                      <TableCell className="text-gray-700" id={`cell-patient-${patient.pep_patient_id}-emr`}>{patient.emr_id || '-'}</TableCell>
                      <TableCell>
                        <Button
                          id={`btn-patient-${patient.pep_patient_id}-view`}
                          variant="ghost"
                          aria-label={`View details for patient ${patient.full_name}`}
                          size="icon"
                          className="text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                          onClick={() => onPatientClick(patient)}
                        >
                          <ExternalLink className="h-4 w-4" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>

            {patients.length === 0 && (
              <div id="no-results-message" className="text-center py-12">
                <Search className="h-12 w-12 text-gray-300 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-gray-900 mb-2">No patients found</h3>
                <p className="text-gray-500">Try searching with different criteria.</p>
              </div>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
};

export default PatientTable;
